import { Link } from 'react-router-dom';
import useTranslation from '../../customHooks/useTranslation';
import useLanguage from '../../customHooks/useLanguage';
import { Sheet, SheetHeading } from './Sheet';
import { useBooking } from '../../contextApi/BookingContext';

// A drawing detail callout: the detail letter over the sheet it lives on.
function DetailMarker({ letter, sheet, accent = false }: { letter: string; sheet: string; accent?: boolean }) {
	return (
		<svg viewBox='0 0 56 56' className='h-12 w-12 shrink-0' aria-hidden='true'>
			<circle cx='28' cy='28' r='26.5' fill='none' strokeWidth='1.25' className={accent ? 'stroke-redline' : 'stroke-ink-soft'} />
			<line x1='1.5' y1='28' x2='54.5' y2='28' strokeWidth='1.25' className={accent ? 'stroke-redline' : 'stroke-ink-soft'} />
			<text x='28' y='22' textAnchor='middle' fontSize='16' fontWeight='700' className={accent ? 'fill-redline' : 'fill-ink'}>
				{letter}
			</text>
			<text x='28' y='43' textAnchor='middle' fontSize='10' fontWeight='600' letterSpacing='0.5' className='fill-ink-faint'>
				{sheet}
			</text>
		</svg>
	);
}

export default function Cooperation() {
	const t = useTranslation();
	const c = t.cooperation;
	const { setTopic } = useBooking();
	const { language } = useLanguage();

	return (
		<Sheet id='cooperation' code='A-101' title={t.nav.sheets.cooperation} className='drafting-grid'>
			<div className='mx-auto grid max-w-[1320px] gap-x-12 gap-y-12 px-4 py-20 sm:px-8 lg:grid-cols-12 lg:py-28'>
				<div className='lg:col-span-4'>
					<div className='lg:sticky lg:top-28'>
						<SheetHeading id='cooperation'>{c.heading}</SheetHeading>
						<p className='mt-5 max-w-[40ch] text-lg leading-relaxed text-ink-soft'>{c.intro}</p>
					</div>
				</div>

				<div className='lg:col-span-8'>
					<article className='border border-ink-line bg-sheet-deep/60 p-6 sm:p-10'>
						<div className='flex flex-wrap items-center gap-x-5 gap-y-3'>
							<DetailMarker letter='A' sheet='A-101' accent />
							<h3 className='type-wide text-[clamp(1.6rem,2.6vw,2.25rem)] font-bold tracking-[-0.02em] text-ink'>{c.consult.name}</h3>
							<span className='lettering border border-redline px-2 py-1 text-label text-redline'>{c.first}</span>
						</div>
						<p className='mt-5 max-w-[60ch] text-lg leading-relaxed text-ink-soft'>{c.consult.audience}</p>

						<ol className='relative mt-10 grid gap-8 sm:grid-cols-3 sm:gap-6'>
							<span aria-hidden='true' className='absolute left-4 right-4 top-4 hidden h-px bg-ink-line sm:block' />
							{c.consult.steps.map((step, i) => (
								<li key={step.title} className='relative'>
									<span className='relative flex h-8 w-8 items-center justify-center rounded-full border border-ink-soft bg-sheet-deep text-sm font-bold text-ink'>{i + 1}</span>
									<h4 className='lettering mt-4 text-label text-ink-faint'>{step.title}</h4>
									<p className='mt-1.5 text-ink'>{step.text}</p>
									{step.areas && (
										<ul className='mt-3 flex flex-wrap gap-1.5'>
											{step.areas.map(area => (
												<li key={area} className='border border-ink-line px-2 py-0.5 text-sm text-ink-soft'>
													{area}
												</li>
											))}
										</ul>
									)}
								</li>
							))}
						</ol>

						<a href='#book' onClick={() => setTopic(0)} className='btn-redline mt-10'>
							{c.consult.cta}
						</a>
					</article>

					<div className='grid border-x border-b border-ink-line sm:grid-cols-2'>
						<article className='p-6 sm:p-8'>
							<div className='flex items-center gap-4'>
								<DetailMarker letter='B' sheet='A-101' />
								<h3 className='type-wide text-xl font-bold tracking-[-0.015em] text-ink'>{c.mentor.name}</h3>
							</div>
							<p className='mt-4 leading-relaxed text-ink-soft'>{c.mentor.audience}</p>
							<ul className='mt-5 divide-y divide-ink-line/70 border-y border-ink-line/70'>
								{c.mentor.topics.map(topic => (
									<li key={topic} className='flex items-center gap-3 py-2 text-ink'>
										<span aria-hidden='true' className='dash-marker' />
										{topic}
									</li>
								))}
							</ul>
							<Link to={language === 'pl' ? '/mentoring' : '/en/mentoring'} className='btn-line mt-6'>
								{c.mentor.cta}
								<svg viewBox='0 0 16 16' className='h-4 w-4' aria-hidden='true'>
									<path d='M2 8h11M9 4l4 4-4 4' fill='none' stroke='currentColor' strokeWidth='1.6' />
								</svg>
							</Link>
						</article>

						<article className='flex h-full flex-col border-t border-ink-line p-6 sm:border-l sm:border-t-0 sm:p-8'>
							<div className='flex items-center gap-4'>
								<DetailMarker letter='C' sheet='A-201' />
								<h3 className='type-wide text-xl font-bold tracking-[-0.015em] text-ink'>{c.build.name}</h3>
							</div>
							<p className='mt-4 leading-relaxed text-ink-soft'>{c.build.audience}</p>
							<p className='mt-5 flex gap-3 border-y border-ink-line/70 py-3 text-sm leading-relaxed text-ink-faint'>
								<span aria-hidden='true' className='dash-marker mt-[0.7em] shrink-0' />
								{c.build.partners}
							</p>
							<div className='mt-auto pt-6'>
								<a href='#scope' className='btn-line'>
									{c.build.cta}
									<svg viewBox='0 0 16 16' className='h-4 w-4' aria-hidden='true'>
										<path d='M8 2v11M4 9l4 4 4-4' fill='none' stroke='currentColor' strokeWidth='1.6' />
									</svg>
								</a>
							</div>
						</article>
					</div>
				</div>
			</div>
		</Sheet>
	);
}
